import { groupStudies } from './studyFiles.js';
import { mergeStudyIds, newestStudies, studyExports } from './studyMetadata.js';

// Maintain file handles by relative path. Only changed folders/stems are reopened.
export function createLiveStudies(directory, initialStudies, onUpdate = () => {}) {
  const entries = new Map();
  const candidates = new Map(studyExports(initialStudies).map((study) => [study.id, study]));
  let current = initialStudies;
  const stemKey = (path) => path.replace(/\.(rep|qrs)$/i, '').toLowerCase();
  return {
    getStudies: () => current,
    async update(paths, signal, replace = false) {
      if (replace) {
        entries.clear();
        candidates.clear();
      }
      const touched = new Set();
      const ids = new Set();
      for (const path of new Set(paths)) {
        if (signal.aborted) throw new Error('Auto Create stopped.');
        const parts = path.split('/');
        if (
          parts.some((part) => !part || part === '.' || part === '..') ||
          !/\.(rep|qrs)$/i.test(path)
        )
          continue;
        const key = stemKey(path);
        touched.add(key);
        if (/\.qrs$/i.test(path) && /[ab]$/.test(key)) touched.add(key.slice(0, -1));
        let parent = directory;
        try {
          for (const part of parts.slice(0, -1)) parent = await parent.getDirectoryHandle(part);
          const name = parts.at(-1);
          const handle = await parent.getFileHandle(name);
          entries.set(path, { file: { name, handle }, relativePath: `${directory.name}/${path}` });
        } catch (error) {
          if (error.name !== 'NotFoundError') throw error;
          entries.delete(path);
        }
      }
      // Group cached names only: this does not enumerate or read unrelated files.
      const affected = (study) =>
        touched.has(
          `${study.folder.slice(directory.name.length + 1)}/${study.stem}`
            .replace(/^\//, '')
            .toLowerCase()
        );
      const discovered = groupStudies([...entries.values()]).filter(affected);
      for (const [key, study] of candidates)
        if (affected(study)) {
          if (study.studyId) ids.add(String(study.studyId));
          candidates.delete(key);
        }
      const parsed = await mergeStudyIds(discovered, current, () => signal.aborted);
      for (const study of studyExports(parsed)) {
        candidates.set(study.id, study);
        if (study.studyId) ids.add(String(study.studyId));
      }
      current = newestStudies([...candidates.values()]);
      if (!signal.aborted) onUpdate(current);
      return ids;
    },
  };
}
